import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import arrowWhiteIcon from '../../../assets/img/icons/arrow_right_white.svg';
import ComponentCircleLoader from '../../../components/Loaders/ComponentCircleLoader';
import NoDataComponent from '../../../components/NoDataComponent/NoDataComponent';
import {
    ProjectTableContainer,
    TableItem,
    TableRowAndBudgetCollapseContainer,
} from '../../../styledComponents/ProjectsNew';
import {
    Container,
    TableText,
} from '../../../styledComponents/common';
import {
    ExpandIcon,
    ProjectExpandButton,
} from '../../Projects/projectsStyles';
import {
    CollapseSection,
    TaskStatusButton,
} from '../tasksStyles';
import { CardsContainer } from '../../../styledComponents/Activity';
import { LightgalleryProvider } from 'react-lightgallery';
import SingleCard from '../../Evidence/SingleCard';

const CateringProjectTableContainer = styled(ProjectTableContainer)`
  grid-template-columns: 2fr 1.2fr 1.2fr 1.5fr 1.5fr 0.8fr 0.8fr 1.2fr 1.2fr 45px;
  justify-content: start;
  @media (max-width: 1400px) {
    grid-template-columns: 2fr 1.2fr 1.2fr 1.5fr 1.5fr 0.8fr 0.8fr 1.2fr 1.2fr 45px;
    overflow-x: auto;
  }
`;

const CateringTableItem = styled(TableItem)`
    justify-items: ${({ align }) => align || 'start'};
    text-align: ${({ align }) => align || 'left'};
`;

const EvidenceExpandSection = styled(CollapseSection)`
  height: auto;
  min-height: ${({ open }) => (open ? `200px` : `0`)};
  max-height: ${({ open }) => (open ? `1000px` : `0`)};
  padding: ${({ open }) => (open ? `20px` : `0`)};
  background: #f8f9fc;
  border-top: ${({ open }) => (open ? `1px solid #c2cce1` : `none`)};
`;

const EvidenceTitle = styled.h4`
  font-size: 14px;
  font-weight: 700;
  color: #2f394e;
  margin-bottom: 15px;
  text-transform: uppercase;
`;

const CateringTaskReportList = ({
    cateringTasksList,
    isLoading,
    expandedTaskId,
    setExpandedTaskId,
}) => {
    const { t, i18n } = useTranslation();
    const [taskEvidences, setTaskEvidences] = useState({});
    const [evidenceLoading, setEvidenceLoading] = useState({});

    const handleExpand = async (task) => {
        if (expandedTaskId === task.id) {
            setExpandedTaskId(null);
        } else {
            setExpandedTaskId(task.id);
            if (task.taskevidences && task.taskevidences.length > 0) {
                const formattedEvidences = task.taskevidences.map((ev, index) => ({
                    id: index,
                    image: ev.image,
                    created_at: task.required_start_date, // Fallback if not in ev
                    created_by: task.assignees?.[0] || { first_name: "", last_name: "" },
                    office_name: task.office?.name || "",
                    task_code: task.task_code
                }));
                setTaskEvidences(prev => ({ ...prev, [task.id]: formattedEvidences }));
            }
        }
    };

    if (isLoading) {
        return (
            <Container padding="100px 0">
                <ComponentCircleLoader />
            </Container>
        );
    }

    return (
        <Container style={{ paddingBottom: '20px' }}>
            {cateringTasksList && cateringTasksList.length > 0 ? (
                <>
                    <CateringProjectTableContainer heading table="catering_tasks">
                        <CateringTableItem>
                            <TableText heading>{t('task_name')}</TableText>
                        </CateringTableItem>
                        <CateringTableItem>
                            <TableText heading>{t('task_code')}</TableText>
                        </CateringTableItem>
                        <CateringTableItem>
                            <TableText heading>{t('assignee')}</TableText>
                        </CateringTableItem>
                        <CateringTableItem>
                            <TableText heading>{t('location')}</TableText>
                        </CateringTableItem>
                        <CateringTableItem>
                            <TableText heading>{t('received_time')}</TableText>
                        </CateringTableItem>
                        <CateringTableItem>
                            <TableText heading>{t('no_meals')}</TableText>
                        </CateringTableItem>
                        <CateringTableItem>
                            <TableText heading>{t('emp_note')}</TableText>
                        </CateringTableItem>
                        <CateringTableItem align="center">
                            <TableText heading>{t('status')}</TableText>
                        </CateringTableItem>
                        <CateringTableItem align="center">
                            <TableText heading>{t('check_out_reason')}</TableText>
                        </CateringTableItem>
                        <CateringTableItem />
                    </CateringProjectTableContainer>
                    {cateringTasksList.map((task) => {
                        const checkoutEvents = task.checkinout?.filter(event => event.event === 'checkout') || [];
                        const latestCheckout = checkoutEvents.length > 0
                            ? checkoutEvents.reduce((a, b) => (a.timestamp > b.timestamp ? a : b))
                            : null;
                        const checkoutEvent = latestCheckout;
                        const assignee = task.assignees?.[0];
                        const assigneeName = assignee ? `${assignee.first_name || ''} ${assignee.last_name || ''}`.trim() : '-';

                        return (
                            <TableRowAndBudgetCollapseContainer key={task.id}>
                                <CateringProjectTableContainer table="catering_tasks">
                                    <CateringTableItem>
                                        <TableText fontWeight={expandedTaskId === task.id ? '600' : '400'}>
                                            {task.name || '-'}
                                        </TableText>
                                    </CateringTableItem>
                                    <CateringTableItem>
                                        <TableText color="#6b7280">{task.task_code || '-'}</TableText>
                                    </CateringTableItem>
                                    <CateringTableItem>
                                        <TableText>{assigneeName}</TableText>
                                    </CateringTableItem>
                                    <CateringTableItem>
                                        <TableText color="#6b7280">{task.location?.name || '-'}</TableText>
                                    </CateringTableItem>
                                    <CateringTableItem>
                                        <TableText color="#6b7280">
                                            {latestCheckout?.timestamp
                                                ? moment.unix(latestCheckout.timestamp).format('M/D/YY h:mm A')
                                                : '-'
                                            }
                                        </TableText>
                                    </CateringTableItem>
                                    <CateringTableItem>
                                        <TableText>{task.number_of_meals || 0}</TableText>
                                    </CateringTableItem>
                                    <CateringTableItem>
                                        <TableText title={task.employee_note}>{task.employee_note || '-'}</TableText>
                                    </CateringTableItem>
                                    <CateringTableItem align="center">
                                        <TaskStatusButton
                                            status={task.status}
                                            style={{
                                                width: '110px',
                                                height: '24px',
                                                borderRadius: '6px',
                                                fontSize: '11px',
                                                fontWeight: '600'
                                            }}
                                        >
                                            {task.status === 'completed'
                                                ? t('complete')
                                                : task.status === 'totally missed'
                                                    ? t('totally_missed')
                                                    : task.status === 'not completed'
                                                        ? t('not_completed')
                                                        : t(task.status)}
                                        </TaskStatusButton>
                                    </CateringTableItem>
                                    <CateringTableItem align="center">
                                        <TableText color="#6b7280">{checkoutEvent?.check_out_reason || '-'}</TableText>
                                    </CateringTableItem>
                                    <CateringTableItem>
                                        <ProjectExpandButton
                                            onClick={() => handleExpand(task)}
                                        >
                                            <ExpandIcon
                                                src={arrowWhiteIcon}
                                                alt=""
                                                open={expandedTaskId === task.id}
                                            />
                                        </ProjectExpandButton>
                                    </CateringTableItem>
                                </CateringProjectTableContainer>
                                <EvidenceExpandSection open={expandedTaskId === task.id}>
                                    {evidenceLoading[task.id] ? (
                                        <ComponentCircleLoader size={30} />
                                    ) : (
                                        <>
                                            <EvidenceTitle>
                                                {t('evidence_images')} ({taskEvidences[task.id]?.length || 0})
                                            </EvidenceTitle>
                                            <LightgalleryProvider
                                                lightgallerySettings={{
                                                    width: '100%',
                                                    height: '100%',
                                                    download: false,
                                                }}
                                            >
                                                <CardsContainer>
                                                    {taskEvidences[task.id] && taskEvidences[task.id].length > 0 ? (
                                                        taskEvidences[task.id].map((item, i) => (
                                                            <SingleCard
                                                                key={i}
                                                                evidenceItem={item}
                                                                imageId={item.id}
                                                                image={item.image}
                                                                timestamp={moment(item.created_at).local().format('h:mm A, Do MMM, YYYY')}
                                                                user={item.created_by.first_name ? `${item.created_by.first_name} ${item.created_by.last_name || ''}` : '-'}
                                                                officeNumber={item.office_name}
                                                                taskCode={item.task_code}
                                                            />
                                                        ))
                                                    ) : (
                                                        <NoDataComponent
                                                            title={t('no_evidence_found')}
                                                            padding="20px 0"
                                                            imageHeight="80px"
                                                        />
                                                    )}
                                                </CardsContainer>
                                            </LightgalleryProvider>
                                        </>
                                    )}
                                </EvidenceExpandSection>
                            </TableRowAndBudgetCollapseContainer>
                        );
                    })}
                </>
            ) : (
                <NoDataComponent
                    title={t('no_tasks_found')}
                    padding="5vh 0"
                    imageHeight="140px"
                />
            )
            }
        </Container >
    );
};

export default CateringTaskReportList;
